import React from "react";
import useSWR from 'swr';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation } from "swiper";
import { SRLWrapper } from "simple-react-lightbox";

// Register Swiper modules
SwiperCore.use([Navigation]);

// API configuration constants
const API_URL = `${process.env.REACT_APP_API_FOLDER}get_galleries/`;
const API_TOKEN = process.env.REACT_APP_API_TOKEN;

// SWR fetcher
const fetcher = (url) =>
  fetch(url, { headers: { "X-API-TOKEN": API_TOKEN } })
    .then((res) => {
      if (!res.ok) throw new Error(`Network response was not ok: ${res.status}`);
      return res.json();
    });

const GalleryWrapper = () => {
  // Data fetching via SWR
  const { data: galleries = [], error, isLoading } = useSWR(API_URL, fetcher);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="gallery-wrapper">
        <div className="container">
          {Array(3).fill().map((_, idx) => (
            <div key={idx} className="gallery-group pt-110">
              <Skeleton height={30} width="30%" style={{ marginBottom: '1rem' }} />
              <Skeleton height={300} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    console.error("API Error:", error);
    return <p className="text-danger">Error: {error.message}</p>;
  }

  // Empty state
  if (!galleries.length) {
    return <p>No galleries found.</p>;
  }

  // Base Swiper config
  const baseSliderConfig = {
    slidesPerView: 1,
    speed: 1000,
    spaceBetween: 24,
    loop: true,
    centeredSlides: true,
    roundLengths: true,
    autoplay: { delay: 9000 },
    breakpoints: {
      480: { slidesPerView: 2 },
      768: { slidesPerView: 2 },
      992: { slidesPerView: 3 },
      1200: { slidesPerView: 3 },
    },
  };

  return (
    <SRLWrapper>
      <div className="gallery-wrapper">
        <div className="container">
          {galleries.map((gallery, gIndex) => {
            const navPrevClass = `gallery-prev-${gIndex}`;
            const navNextClass = `gallery-next-${gIndex}`;
            const sliderConfig = {
              ...baseSliderConfig,
              navigation: {
                prevEl: `.${navPrevClass}`,
                nextEl: `.${navNextClass}`,
              },
            };
            const imageBase = `${process.env.PUBLIC_URL}/images/gallery/${gallery.folder_name}`;

            return (
              <div key={gallery.id} className="gallery-group pt-110">
              <div className="gallery-group-header d-flex justify-content-between align-items-center">
                <h4 className="gallery-group-title mb-0">{gallery.title}</h4>
                <div className="gallery-arrows text-center d-lg-block d-none">
                <div className={`gallery-button-prev ${navPrevClass}`} tabIndex={0} role="button" aria-label="Previous slide">
                  <i className="bi bi-chevron-double-left" />
                </div>
                <div className={`gallery-button-next ${navNextClass}`} tabIndex={0} role="button" aria-label="Next slide">
                  <i className="bi bi-chevron-double-right" />
                </div>
                </div>
              </div>

              <Swiper {...sliderConfig} className="swiper gallery-slider mt-4">
                {gallery.images
                .reduce((chunks, img, index) => {
                  if (index % 2 === 0) {
                  chunks.push([img]);
                  } else {
                  chunks[chunks.length - 1].push(img);
                  }
                  return chunks;
                }, [])
                .map((imgPair, slideIndex) => (
                  <SwiperSlide key={slideIndex}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {imgPair.map((img) => (
                    <a key={img.id} href={`${imageBase}/${img.fullsize}`} data-attribute="SRL" className="gallery-item">
                      <img src={`${imageBase}/thumbs/${img.thumb}`} alt={`${gallery.title} – ${img.id}`} />
                    </a>
                    ))}
                  </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              </div>
            );
          })}
        </div>
      </div>
    </SRLWrapper>
  );
};

export default GalleryWrapper;
